// @ts-nocheck
import TwoButtonModal from "@store/Overrides/Components/TwoButtonModal";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox, { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import useAreProductsLoading from "@store/Framework/Common/Hooks/useAreProductsLoading";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import useBulkLists from "@store/Library/Widgets/BulkLists/useBulkLists";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useState } from "react";
import { css } from "styled-components";

interface BulkListButtonStyles {
    wrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
    addButton?: ButtonPresentationProps;
    removeButton?: ButtonPresentationProps;
    resultsWrapper?: InjectableCss;
    selectAll?: CheckboxPresentationProps;
    unSelectAll?: ButtonPresentationProps;
    listCount?: TypographyPresentationProps;
}

export const bulkListButtonStyles: BulkListButtonStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-inline: 1rem;
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            gap: 1rem;
        `,
    },
    addButton: {
        variant: "primary",
    },
    removeButton: {
        variant: "primary",
    },
    resultsWrapper: resultCountProps.wrapper,
    selectAll: resultCountProps.selectAll,
    unSelectAll: {
        variant: "tertiary",

        css: css`
            ${inlineSeparatorStyles(pxToRem(23), pxToRem(1), "common.border")}
            display: flex;
            align-items: center;
            font-size: 14px;
            gap: 0.5rem;
            border: none;
            padding: 0;
            background: none;
            margin-bottom: ${pxToRem(3)};
            min-height: unset;
            line-height: 15px;
            &:disabled {
                color: ${getColor("text.accent")};
                svg {
                    color: ${getColor("text.accent")};
                }
            }
        `,
    },
    listCount: {
        css: css`
            ${inlineSeparatorStyles(pxToRem(23), pxToRem(1), "common.border")}
        `,
    },
};

const BulkListButtons = () => {
    const {
        isLoading,
        idsBySelectedItems,
        handleAddProductToLists,
        handleRemoveProductFromLists,
        selectorProductId,
        toggleAllFilteredItems,
        filteredListIds,
        selectedListIds,
        handleDeSelectAllItems,
        showSelected,
        handleToggleShowSelected,
        productName,
    } = useBulkLists();
    const [addModalIsOpen, setAddModalIsOpen] = useState(false);
    const [removeModalIsOpen, setRemoveModalIsOpen] = useState(false);
    const areProductsLoading = useAreProductsLoading();
    const idsWithProductCount = idsBySelectedItems.idsWithProduct.length;
    const idsWithoutProductCount = idsBySelectedItems.idsWithoutProduct.length;
    const isAllSelected = selectedListIds.length !== 0 && filteredListIds.length === selectedListIds.length;
    const totalListsCount = filteredListIds.length;
    const selectedListCount = selectedListIds.length;

    const handleRemoveProductConfirm = () => {
        setRemoveModalIsOpen(false);
        handleRemoveProductFromLists();
    };
    const handleAddProductConfirm = () => {
        setAddModalIsOpen(false);
        handleAddProductToLists();
    };

    const getMessageText = (
        type: "Add" | "Remove",
        productName: string,
        selectedCount: number,
        totalListCount: number,
    ) => {
        return translate(
            `${type} {0} ${type === "Add" ? "to" : "from"} {1} list(s) out of {2} selected list(s)`,
            productName,
            selectedCount.toString(),
            totalListCount.toString(),
        );
    };

    return (
        <FadeIn animate appear>
            <LoadingOverlay loading={isLoading || areProductsLoading}>
                <StyledWrapper {...bulkListButtonStyles.wrapper}>
                    <StyledWrapper {...bulkListButtonStyles.resultsWrapper}>
                        <Checkbox
                            {...bulkListButtonStyles.selectAll}
                            checked={isAllSelected}
                            onChange={toggleAllFilteredItems}
                            data-test-selector="selectAllItems"
                            disabled={showSelected}
                        >
                            {translate("Select All")}
                        </Checkbox>

                        <Button
                            disabled={selectedListCount === 0}
                            {...bulkListButtonStyles.unSelectAll}
                            onClick={handleDeSelectAllItems}
                        >
                            <Icon size={15} src={XCircle} /> {translate("Unselect All")}
                        </Button>

                        <Checkbox
                            {...bulkListButtonStyles.selectAll}
                            checked={showSelected}
                            onChange={() => handleToggleShowSelected()}
                            disabled={selectedListCount === 0}
                        >
                            {translate("Show Selected")}
                        </Checkbox>

                        <Typography {...bulkListButtonStyles.listCount}>{`${totalListsCount} ${translate(
                            "Lists",
                        )}`}</Typography>
                        {selectedListIds.length !== 0 && (
                            <Typography {...bulkListButtonStyles.listCount}>{`${selectedListCount} ${translate(
                                "Selected",
                            )}`}</Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...bulkListButtonStyles.buttonContainer}>
                        <Button
                            disabled={!idsWithoutProductCount || !selectorProductId}
                            {...bulkListButtonStyles.addButton}
                            onClick={() => setAddModalIsOpen(true)}
                            sizeVariant="small"
                        >
                            {translate("Add")}
                        </Button>

                        <Button
                            disabled={!idsWithProductCount || !selectorProductId}
                            {...bulkListButtonStyles.removeButton}
                            sizeVariant="small"
                            onClick={() => setRemoveModalIsOpen(true)}
                        >
                            {translate("Remove")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </LoadingOverlay>
            <TwoButtonModal
                modalIsOpen={addModalIsOpen}
                headlineText={translate("Add Product to Lists")}
                messageText={getMessageText("Add", productName, idsWithoutProductCount, selectedListCount)}
                submitButtonText={translate("Add")}
                cancelButtonText={translate("Cancel")}
                onSubmit={handleAddProductConfirm}
                onCancel={() => setAddModalIsOpen(false)}
            />
            <TwoButtonModal
                modalIsOpen={removeModalIsOpen}
                headlineText={translate("Remove Product from Lists")}
                messageText={getMessageText("Remove", productName, idsWithProductCount, selectedListCount)}
                submitButtonText={translate("Remove")}
                cancelButtonText={translate("Cancel")}
                onSubmit={handleRemoveProductConfirm}
                onCancel={() => setRemoveModalIsOpen(false)}
            />
        </FadeIn>
    );
};

const widgetModule: WidgetModule = {
    component: BulkListButtons,
    definition: {
        group: "Common",
        icon: "File",
    },
};

export default widgetModule;
